/**
 * Parses the parameters from a given URL.
 * @param url - The URL to parse parameters from.
 * @returns An array of parsed parameters, or null if no parameters were found.
 */
function parseParams(url: string) {
	const regex = /_(.*?)\./;
	const found = url.match(regex);
	if (found && found[1]) {
		return found[1].split('_');
	} else {
		return null;
	}
}

/**
 * Checks if the given URL is for the GDB website.
 * @param url - The URL to check.
 * @returns True if the URL is for the GDB website, false otherwise.
 */
export function isGdb(url: string) {
	return url.includes('https://gdb.');
}

/**
 * Returns a string with a list of image sources in different sizes for use in a `srcset` attribute.
 * @param sizes - An array of numbers representing the different sizes for the images.
 * @param url - A string representing the URL of the image.
 * @returns A string with a list of image sources in different sizes for use in a `srcset` attribute.
 */
export function getGdbSrcSet(url: string, sizes: number[]) {
	const { clean, ext } = parseGdbUrl(url);
	return sizes
		.map((size) => {
			return `${clean}_w${size}_q60_mw${size}_mh${size}.${ext} ${size}w`;
		})
		.join(', ');
}

/**
 * Returns the default source URL for a given GDB image URL and size.
 * @param url - The GDB image URL to parse.
 * @param size - The desired size of the image.
 * @returns The default source URL for the given GDB image URL and size.
 */
export function getGdbDefaultSrc(url: string, size: number) {
	const { clean, ext } = parseGdbUrl(url);
	return `${clean}_w${size}_q60_mw${size}_mh${size}.${ext}`;
}

/**
 * Parses a GDB URL and returns an object containing its base URL, GUID, extension, and parameters.
 * @param url - The GDB URL to parse.
 * @returns An object containing the base URL, GUID, extension, and parameters of the GDB URL.
 */
export function parseGdbUrl(url: string) {
	// base url is the gdb.rferl.org part in https://gdb.rferl.org/01000000-0aff-0242-d47b-08db965329b0.jpg
	const base = url.split('/').slice(0, 3).join('/');
	// guid is the 01000000-0aff-0242-d47b-08db965329b0 part in https://gdb.rferl.org/01000000-0aff-0242-d47b-08db965329b0.jpg
	const guid = url.split('/').slice(3, 4).join('/').split('.')[0];
	const ext = url.split('/').slice(3, 4).join('/').split('.')[1];
	// using regex, get the params which are the values after the first _ until the extension in https://gdb.rferl.org/01000000-0aff-0242-d47b-08db965329b0_w200_h200_q60_mw600_mh800_r1_s.jpg
	const params = parseParams(url);
	// return a string of the "clean" url without any params
	const clean = `${url.split('_')[0]}`;

	return {
		clean,
		base,
		guid,
		ext,
		params,
	};
}
